import { Controller } from "@hotwired/stimulus"

// Post reference autocomplete controller
// Detects [[ in chat input and shows post suggestions
export default class extends Controller {
  static targets = ["input", "dropdown", "list"]

  connect() {
    this.selectedIndex = -1
    this.isOpen = false
    this.searchTimeout = null
  }

  handleInput(event) {
    const input = event.target
    const cursorPosition = input.selectionStart
    const text = input.value.substring(0, cursorPosition)

    // Check if we're in a post reference context [[
    // Match [[ followed by optional board:title or just title
    const refMatch = text.match(/\[\[([^\]]*?)$/)

    if (refMatch) {
      const query = refMatch[1]
      if (query.length >= 1) {
        this.debouncedSearch(query)
      } else {
        // Show initial suggestions when just [[ is typed
        this.searchPosts("")
      }
    } else {
      this.hideSuggestions()
    }
  }

  handleKeydown(event) {
    if (!this.isOpen) return

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        event.stopPropagation()
        this.moveSelection(1)
        break
      case "ArrowUp":
        event.preventDefault()
        event.stopPropagation()
        this.moveSelection(-1)
        break
      case "Enter":
      case "Tab":
        if (this.selectedIndex >= 0) {
          event.preventDefault()
          event.stopPropagation()
          this.selectCurrentItem()
        }
        break
      case "Escape":
        event.preventDefault()
        this.hideSuggestions()
        break
      case "]":
        // If typed ]] close the dropdown
        if (this.isOpen) {
          this.hideSuggestions()
        }
        break
    }
  }

  debouncedSearch(query) {
    clearTimeout(this.searchTimeout)
    this.searchTimeout = setTimeout(() => {
      this.searchPosts(query)
    }, 200)
  }

  async searchPosts(query) {
    try {
      let searchUrl = "/api/v1/posts/search?"
      let searchQuery = query
      let board = ""

      // Parse board:title format
      if (query.includes(":")) {
        const [boardPart, titlePart] = query.split(":", 2)
        board = boardPart
        searchQuery = titlePart || ""
      }

      const params = new URLSearchParams()
      if (searchQuery) params.append("q", searchQuery)
      if (board) params.append("board", board)
      params.append("limit", "10")

      const response = await fetch(searchUrl + params.toString())

      if (response.ok) {
        const posts = await response.json()
        this.showSuggestions(posts)
      }
    } catch (error) {
      console.error("Failed to search posts:", error)
    }
  }

  showSuggestions(posts) {
    if (!posts || posts.length === 0) {
      this.hideSuggestions()
      return
    }

    this.renderSuggestions(posts)
    this.isOpen = true
    this.selectedIndex = 0
    this.updateSelection()
    this.dropdownTarget.classList.remove("hidden")
  }

  hideSuggestions() {
    this.isOpen = false
    this.selectedIndex = -1
    if (this.hasDropdownTarget) {
      this.dropdownTarget.classList.add("hidden")
    }
  }

  renderSuggestions(posts) {
    this.listTarget.innerHTML = posts.map((post, index) => `
      <li class="px-3 py-2 cursor-pointer hover:bg-purple-50 ${index === this.selectedIndex ? 'bg-purple-100' : ''}"
          data-action="click->post-reference#selectItem"
          data-bo-table="${this.escapeHtml(post.bo_table)}"
          data-subject="${this.escapeHtml(post.wr_subject)}"
          data-board-name="${this.escapeHtml(post.board_name || post.bo_table)}">
        <div class="flex items-center">
          <div class="h-8 w-8 rounded bg-purple-100 flex items-center justify-center mr-3 flex-shrink-0">
            <svg class="h-4 w-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <div class="flex-1 min-w-0">
            <div class="font-medium text-sm text-gray-900 truncate">${this.escapeHtml(post.wr_subject)}</div>
            <div class="text-xs text-gray-500 flex items-center">
              <span class="text-purple-600 font-medium">${this.escapeHtml(post.board_name || post.bo_table)}</span>
              <span class="mx-1">·</span>
              <span>${this.escapeHtml(post.wr_name || '')}</span>
            </div>
          </div>
        </div>
      </li>
    `).join("")
  }

  escapeHtml(text) {
    if (!text) return ''
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  moveSelection(direction) {
    const items = this.listTarget.querySelectorAll("li")
    this.selectedIndex = Math.max(0, Math.min(items.length - 1, this.selectedIndex + direction))
    this.updateSelection()
  }

  updateSelection() {
    const items = this.listTarget.querySelectorAll("li")
    items.forEach((item, index) => {
      item.classList.toggle("bg-purple-100", index === this.selectedIndex)
    })
  }

  selectCurrentItem() {
    const items = this.listTarget.querySelectorAll("li")
    const selected = items[this.selectedIndex]
    if (selected) {
      this.insertReference(selected.dataset.boardName, selected.dataset.subject)
    }
  }

  selectItem(event) {
    const boardName = event.currentTarget.dataset.boardName
    const subject = event.currentTarget.dataset.subject
    this.insertReference(boardName, subject)
  }

  insertReference(boardName, subject) {
    const input = this.inputTarget
    const cursorPosition = input.selectionStart
    const text = input.value

    // Find the [[ position
    const beforeCursor = text.substring(0, cursorPosition)
    const bracketPosition = beforeCursor.lastIndexOf("[[")

    if (bracketPosition >= 0) {
      const before = text.substring(0, bracketPosition)
      const after = text.substring(cursorPosition)
      // Use board_name:subject format for display
      const reference = `[[${boardName}:${subject}]]`
      input.value = `${before}${reference}${after}`
      const newPosition = bracketPosition + reference.length
      input.selectionStart = input.selectionEnd = newPosition
    }

    this.hideSuggestions()
    input.focus()

    // Trigger input event for other controllers
    input.dispatchEvent(new Event('input', { bubbles: true }))
  }
}
